import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import CBBLogo from '../components/cbb-logo'
import { ApiError, apiFetch } from '../lib/api'
import { authClient } from '../lib/authClient'

export function LoginPage() {
	const navigate = useNavigate()
	const queryClient = useQueryClient()
	const [email, setEmail] = useState('')
	const [password, setPassword] = useState('')
	const [error, setError] = useState<string | null>(null)
	const [busy, setBusy] = useState(false)

	async function handleSubmit(e: React.FormEvent) {
		e.preventDefault()
		setBusy(true)
		setError(null)
		try {
			const { error: authError } = await authClient.signIn.email({ email, password })
			if (authError) {
				setError(authError.message ?? 'Invalid email or password')
				return
			}
			// Signing in is not the same as having access: a deactivated admin
			// still gets a session. Confirm with the backend before letting them
			// through, so they see the real reason rather than a blank dashboard.
			await apiFetch('/api/admin/me')
			await queryClient.invalidateQueries()
			navigate('/dashboard', { replace: true })
		} catch (err) {
			setError(err instanceof ApiError ? err.message : 'Could not sign in, please try again')
		} finally {
			setBusy(false)
		}
	}

	return (
		<div className="flex min-h-full items-center justify-center p-5">
			<form onSubmit={handleSubmit} className="card w-full max-w-sm p-6">
				<div className="mb-6 flex flex-col items-center gap-2">
					<CBBLogo monochrome className="h-[42px] w-auto" />
					<h1 className="font-display text-xs font-semibold uppercase tracking-[0.18em] text-ink-faint">
						Admin
					</h1>
				</div>

				<label className="label" htmlFor="email">
					Email
				</label>
				<input
					id="email"
					type="email"
					className="input mb-4"
					value={email}
					onChange={(e) => setEmail(e.target.value)}
					autoComplete="username"
					required
				/>

				<label className="label" htmlFor="password">
					Password
				</label>
				<input
					id="password"
					type="password"
					className="input mb-4"
					value={password}
					onChange={(e) => setPassword(e.target.value)}
					autoComplete="current-password"
					required
				/>

				{error && (
					<p role="alert" className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
						{error}
					</p>
				)}

				<button type="submit" className="btn-primary w-full" disabled={busy}>
					{busy ? 'Signing in…' : 'Sign in'}
				</button>
			</form>
		</div>
	)
}
